/**
 * ConfigurableDrawer - drawer deslizante reutilizavel com painel de
 * personalizacao (campos, acoes built-in, acoes personalizadas, sync
 * de default da organizacao vs config pessoal).
 *
 * Usa `useUIPref` para persistir a config por utilizador e permite ao
 * super-admin gravar como default organizacional.
 *
 * Props:
 *   open, onClose, title, overline, width='md', testid='drawer', footer
 *   uiPrefKey: chave unica ('account_drawer', 'ticket_drawer', ...)
 *   uiPrefVersion: sufixo de versao ('v1', 'v2', ...) para incompatibilidades
 *   fieldDefs: [{ key, label, render(entity) }]
 *   actionDefs: [{ key, label, icon, enabled?(entity), run(entity) }]
 *   defaultActions: [key,...] ordem inicial
 *   defaultActionsVisible: { key: bool }
 *   extraSections: [{ key, label, defaultVisible?, render(entity) }]
 *   entity, placeholders: passados as acoes personalizadas
 *   entityLoading: mostra estado de carregamento
 *   transitions: [{ key, label, onClick }] botoes extra (ex. mudar estado)
 */
import { useMemo, useState } from "react";
import Drawer from "./Drawer";
import { Btn, dt } from "./common";
import useUIPref from "../hooks/useUIPref";
import CustomActionsEditor from "./CustomActionsEditor";
import { runCustomAction } from "../lib/customActions";
import * as Lucide from "lucide-react";
import {
  Settings2, GripVertical, ChevronUp, ChevronDown, ChevronRight,
  RotateCcw, Save, Zap,
} from "lucide-react";

export default function ConfigurableDrawer({
  open, onClose, title, overline, width = "md", testid = "drawer", footer,
  uiPrefKey, uiPrefVersion = "v1",
  fieldDefs = [], actionDefs = [],
  defaultActions, defaultActionsVisible,
  extraSections = [],
  entity, placeholders = {},
  entityLoading = false,
  transitions = [],
}) {
  const DEFAULT_CFG = useMemo(() => ({
    fields: fieldDefs.map((f) => f.key),
    visible: Object.fromEntries(fieldDefs.map((f) => [f.key, true])),
    actions: defaultActions || actionDefs.map((a) => a.key),
    actionsVisible: defaultActionsVisible || Object.fromEntries(actionDefs.map((a) => [a.key, true])),
    customActions: [],
    sectionsVisible: Object.fromEntries(extraSections.map((s) => [s.key, s.defaultVisible !== false])),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [uiPrefKey]);

  const {
    value: cfg, updatePersonal: setCfg, source, saving,
    saveAsOrgDefault, resetToOrgDefault, hasPersonal, hasOrgDefault, orgDefaultMeta, isAdmin,
  } = useUIPref(uiPrefKey, DEFAULT_CFG, { version: uiPrefVersion });
  const [cfgOpen, setCfgOpen] = useState(false);

  const visibleFields = (cfg.fields || DEFAULT_CFG.fields).filter((k) => cfg.visible?.[k])
    .map((k) => fieldDefs.find((f) => f.key === k)).filter(Boolean);
  const orderedActions = (cfg.actions || DEFAULT_CFG.actions).filter((k) => (cfg.actionsVisible || {})[k])
    .map((k) => actionDefs.find((a) => a.key === k)).filter(Boolean);
  const customActions = (cfg.customActions || []).filter((a) => a && a.visible !== false);
  const sectionsVisible = { ...DEFAULT_CFG.sectionsVisible, ...(cfg.sectionsVisible || {}) };

  return (
    <Drawer open={open} onClose={onClose} title={title} overline={overline}
      width={width} testid={testid} footer={footer}>
      <div className="flex justify-end -mt-2 mb-2 relative">
        <button onClick={() => setCfgOpen((v) => !v)} data-testid={`${testid}-configure`}
          className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-navy hover:bg-slate-100 rounded px-2 py-1 transition-colors">
          <Settings2 size={12} /> Configurar
        </button>
        {cfgOpen && (
          <ConfigurePanel
            testid={testid}
            cfg={cfg} setCfg={setCfg} onClose={() => setCfgOpen(false)}
            defaults={DEFAULT_CFG}
            fieldDefs={fieldDefs} actionDefs={actionDefs} extraSections={extraSections}
            source={source} saving={saving}
            hasPersonal={hasPersonal} hasOrgDefault={hasOrgDefault} orgDefaultMeta={orgDefaultMeta}
            isAdmin={isAdmin}
            onSaveAsOrgDefault={saveAsOrgDefault} onResetToOrgDefault={resetToOrgDefault}
            samplePlaceholders={placeholders}
          />
        )}
      </div>

      {entityLoading && <div className="text-sm text-slate-500">A carregar...</div>}
      {!entityLoading && entity && (
        <div className="space-y-5">
          {(orderedActions.length > 0 || customActions.length > 0 || transitions.length > 0) && (
            <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-actions`}>
              {orderedActions.map((a) => {
                const enabled = a.enabled ? a.enabled(entity) : true;
                const Icon = a.icon;
                return (
                  <button key={a.key} onClick={() => enabled && a.run(entity)} disabled={!enabled}
                    title={a.label + (!enabled ? " (sem dados)" : "")}
                    data-testid={`${testid}-action-${a.key}`}
                    className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded-md border transition-colors ${enabled ? "bg-white text-slate-700 border-slate-300 hover:bg-blue hover:text-white hover:border-blue" : "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed"}`}>
                    {Icon && <Icon size={12} />} {a.label}
                  </button>
                );
              })}
              {customActions.map((a) => {
                const Icon = Lucide[a.icon] || Zap;
                return (
                  <button key={a.id} onClick={() => runCustomAction(a, placeholders)}
                    title={a.label}
                    data-testid={`${testid}-custom-action-${a.id}`}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded-md border bg-blue/5 text-blue border-blue/30 hover:bg-blue hover:text-white hover:border-blue transition-colors">
                    <Icon size={12} /> {a.label}
                  </button>
                );
              })}
              {transitions.map((t) => (
                <button key={t.key} onClick={t.onClick}
                  data-testid={`${testid}-transition-${t.key}`}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors">
                  <ChevronRight size={12} /> {t.label}
                </button>
              ))}
            </div>
          )}

          {visibleFields.length > 0 && (
            <div className="grid grid-cols-2 gap-3" data-testid={`${testid}-fields`}>
              {visibleFields.map((f) => (
                <div key={f.key} data-testid={`${testid}-field-${f.key}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{f.label}</div>
                  <div className="text-[13px] text-navy mt-0.5 break-words">{f.render(entity)}</div>
                </div>
              ))}
            </div>
          )}

          {extraSections.filter((s) => sectionsVisible[s.key]).map((s) => (
            <div key={s.key} className="border-t border-slate-200 pt-3" data-testid={`${testid}-section-${s.key}`}>
              <div className="text-navy font-semibold text-[13px] mb-2">{s.label}</div>
              {s.render(entity, cfg)}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}


function ConfigurePanel({
  testid, cfg, setCfg, onClose, defaults, fieldDefs, actionDefs, extraSections,
  source, saving, hasPersonal, hasOrgDefault, orgDefaultMeta, isAdmin,
  onSaveAsOrgDefault, onResetToOrgDefault, samplePlaceholders,
}) {
  const tabs = extraSections.length > 0
    ? ["campos", "acoes", "personalizadas", "seccoes", "sync"]
    : ["campos", "acoes", "personalizadas", "sync"];
  const [tab, setTab] = useState("campos");

  const move = (list, idx, dir, kSetter) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = [...list]; const [it] = next.splice(idx, 1); next.splice(to, 0, it); kSetter(next);
  };
  const toggleField = (key) => setCfg({ visible: { ...cfg.visible, [key]: !cfg.visible[key] } });
  const toggleAction = (key) => setCfg({ actionsVisible: { ...(cfg.actionsVisible || {}), [key]: !(cfg.actionsVisible || {})[key] } });
  const toggleSection = (key) => setCfg({ sectionsVisible: { ...(cfg.sectionsVisible || {}), [key]: !(cfg.sectionsVisible || {})[key] } });
  const resetToBuiltin = () => setCfg({ ...defaults });
  const sourceLabel = { personal: "config pessoal", org: "default da organizacao", default: "default do sistema", local: "cache local" }[source] || source;
  const sourceCls = source === "personal" ? "bg-blue/10 text-blue border-blue/30"
    : source === "org" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <div className="absolute right-0 top-full mt-1 z-40 w-[440px] bg-white border border-slate-200 rounded-lg shadow-xl"
      data-testid={`${testid}-configure-panel`}>
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-navy">Personalizar drawer</div>
          <div className="flex items-center gap-1 mt-0.5">
            <span data-testid={`${testid}-cfg-source`} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${sourceCls}`}>{sourceLabel}</span>
            {saving && <span className="text-[10px] text-slate-400 italic">a guardar...</span>}
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none">&times;</button>
      </div>
      <div className="border-b border-slate-200 flex text-[12px]" data-testid={`${testid}-cfg-tabs`}>
        {tabs.map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`${testid}-cfg-tab-${k}`}
            className={`flex-1 py-1.5 font-medium capitalize ${tab === k ? "text-blue border-b-2 border-blue" : "text-slate-500 hover:text-navy"}`}>{k}</button>
        ))}
      </div>
      <div className="max-h-96 overflow-y-auto py-1">
        {tab === "campos" && (cfg.fields || defaults.fields).map((k, idx, list) => {
          const f = fieldDefs.find((x) => x.key === k);
          if (!f) return null;
          return (
            <div key={k} className="flex items-center gap-1.5 px-2 py-1 hover:bg-slate-50">
              <GripVertical size={12} className="text-slate-300 shrink-0" />
              <label className="flex-1 flex items-center gap-2 text-[12px] text-navy cursor-pointer">
                <input type="checkbox" checked={!!cfg.visible?.[k]} onChange={() => toggleField(k)}
                  data-testid={`${testid}-cfg-field-${k}`} className="rounded border-slate-300" />
                {f.label}
              </label>
              <button onClick={() => move(list, idx, -1, (l) => setCfg({ fields: l }))} disabled={idx === 0}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronUp size={12} /></button>
              <button onClick={() => move(list, idx, 1, (l) => setCfg({ fields: l }))} disabled={idx === list.length - 1}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronDown size={12} /></button>
            </div>
          );
        })}
        {tab === "acoes" && (cfg.actions || defaults.actions).map((k, idx, list) => {
          const a = actionDefs.find((x) => x.key === k);
          if (!a) return null;
          const Icon = a.icon;
          return (
            <div key={k} className="flex items-center gap-1.5 px-2 py-1 hover:bg-slate-50">
              <GripVertical size={12} className="text-slate-300 shrink-0" />
              <label className="flex-1 flex items-center gap-2 text-[12px] text-navy cursor-pointer">
                <input type="checkbox" checked={!!(cfg.actionsVisible || {})[k]} onChange={() => toggleAction(k)}
                  data-testid={`${testid}-cfg-action-${k}`} className="rounded border-slate-300" />
                {Icon && <Icon size={12} className="text-slate-500" />} {a.label}
              </label>
              <button onClick={() => move(list, idx, -1, (l) => setCfg({ actions: l }))} disabled={idx === 0}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronUp size={12} /></button>
              <button onClick={() => move(list, idx, 1, (l) => setCfg({ actions: l }))} disabled={idx === list.length - 1}
                className="text-slate-400 hover:text-navy disabled:opacity-30 p-0.5"><ChevronDown size={12} /></button>
            </div>
          );
        })}
        {tab === "personalizadas" && (
          <CustomActionsEditor value={cfg.customActions || []}
            onChange={(next) => setCfg({ customActions: next })}
            samplePlaceholders={samplePlaceholders || {}} />
        )}
        {tab === "seccoes" && (
          <div className="space-y-1 px-2 py-2">
            {extraSections.map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-[12px] text-navy cursor-pointer">
                <input type="checkbox" checked={cfg.sectionsVisible?.[s.key] !== false} onChange={() => toggleSection(s.key)}
                  data-testid={`${testid}-cfg-section-${s.key}`} className="rounded border-slate-300" />
                Mostrar {s.label.toLowerCase()}
              </label>
            ))}
          </div>
        )}
        {tab === "sync" && (
          <div className="space-y-2 px-3 py-2 text-[12px] text-slate-700">
            <p>Alteracoes gravam automaticamente na sua conta (config pessoal).</p>
            <div className="rounded bg-slate-50 border border-slate-200 p-2">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Default da organizacao</div>
              {hasOrgDefault
                ? <div className="text-[12px] text-navy">Existe. Por {orgDefaultMeta?.por || "-"} em {orgDefaultMeta?.em ? dt(orgDefaultMeta.em) : "-"}.</div>
                : <div className="text-[12px] text-slate-500 italic">Sem default definido. Todos os utilizadores comecam com o default do sistema.</div>}
            </div>
            <button onClick={onResetToOrgDefault} disabled={!hasPersonal || saving}
              data-testid={`${testid}-cfg-reset-personal`}
              className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-medium border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
              <RotateCcw size={12} /> Repor para {hasOrgDefault ? "default da organizacao" : "default do sistema"}
            </button>
            {isAdmin && (
              <div className="border-t border-slate-200 pt-2 mt-2">
                <div className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wider mb-1">Admin</div>
                <button onClick={onSaveAsOrgDefault} disabled={saving}
                  data-testid={`${testid}-cfg-save-as-org`}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-60">
                  <Save size={12} /> Guardar como default da organizacao
                </button>
                <p className="text-[10px] text-slate-500 mt-1">Todos os utilizadores sem config pessoal passam a ver esta.</p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="border-t border-slate-200 px-2 py-1.5 flex justify-between">
        <button onClick={resetToBuiltin} data-testid={`${testid}-cfg-reset`} className="text-[11px] text-slate-500 hover:text-navy">Repor default do sistema</button>
        <button onClick={onClose} className="text-[11px] text-blue font-medium hover:underline">Concluir</button>
      </div>
    </div>
  );
}
